import React, { useState, createContext, useContext } from 'react';
type Lang = 'en' | 'es';
interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}
const translations: Record<Lang, Record<string, string>> = {
  en: {
    // Navbar
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.about': 'About',
    'nav.reviews': 'Reviews',
    'nav.contact': 'Contact',
    'nav.quote': 'GET FREE QUOTE',
    // Brand
    'brand.tagline': 'PREMIUM CLEANING SERVICES',
    // Hero
    'hero.badge': '✦ MIAMI & BROWARD AREA · SAME-DAY AVAILABILITY',
    'hero.title1': 'Your Home, Cleaned',
    'hero.title2': 'With Personal Care.',
    'hero.sub': 'Premium home cleaning in Miami & Broward. Licensed, reliable and detail-oriented. Same-day availability — ask about recurring discounts.',
    'hero.btn1': '📱 BOOK NOW — FREE ESTIMATE',
    'hero.btn2': 'Explore Our Services ↓',
    'hero.trust1': 'Licensed & Insured',
    'hero.trust2': 'Same-Day Available',
    'hero.trust3': '100% Satisfaction Guaranteed',
    // Trust Bar
    'trust.local': 'Licensed & Reliable',
    'trust.area': 'Miami & Broward Area',
    'trust.hours': 'Same-Day Availability',
    'trust.payment': 'Recurring Discounts',
    'trust.services': '5 Services Available',
    // Services
    'services.label': '✦ OUR SERVICES ✦',
    'services.title': 'Premium Cleaning For Every Space',
    'services.sub': 'Licensed · Reliable · Detail-Oriented',
    'services.c1.title': 'Standard Cleaning',
    'services.c1.desc': 'Reliable, consistent cleaning to keep your home looking its best week after week.',
    'services.c2.title': 'Deep Cleaning',
    'services.c2.desc': 'Top-to-bottom thorough cleaning for homes that need a complete reset.',
    'services.c3.title': 'Airbnb Cleaning',
    'services.c3.desc': 'Fast, hotel-quality turnovers so your guests always arrive to a spotless stay.',
    'services.c4.title': 'Move-In / Move-Out',
    'services.c4.desc': 'Leave your old space spotless or arrive to a perfectly clean new home.',
    'services.c5.title': 'Office Cleaning',
    'services.c5.desc': 'Professional, detail-oriented cleaning for offices and commercial spaces.',
    'services.cta': 'GET A FREE ESTIMATE TODAY',
    // Before & After
    'ba.label': '✦ OUR WORK ✦',
    'ba.title': 'Real Results. Real Homes.',
    'ba.sub': 'See the TwoGirls MopCo difference in every room we touch.',
    'ba.stat1': 'Satisfaction Guaranteed',
    'ba.stat2': 'Same-Day Available',
    'ba.stat3': 'Premium Quality',
    'ba.cta': '📱 BOOK YOUR CLEANING TODAY',
    // Why Choose Us
    'why.title1': 'Why Miami & Broward Trusts',
    'why.title2': 'TwoGirls MopCo',
    'why.c1.title': 'Licensed & Insured',
    'why.c1.desc': 'Fully licensed and insured for your complete peace of mind.',
    'why.c2.title': 'Same-Day Available',
    'why.c2.desc': 'Need cleaning today? We make it happen.',
    'why.c3.title': 'Recurring Discounts',
    'why.c3.desc': 'The more you book, the more you save. Ask us about our plans.',
    'why.c4.title': 'Detail-Oriented',
    'why.c4.desc': "We don't miss a corner. Every space gets our full attention, always.",
    // Special Offer
    'offer.title': 'Ask About Our Recurring Discounts',
    'offer.sub': 'The more you book, the more you save. Same-day availability for all Miami & Broward.',
    'offer.cta': '📱 CALL (786) 660-3720',
    // Reviews
    'rev.based': 'Based on verified client reviews',
    'rev.c1.text': 'TwoGirls MopCo is the best! They are so thorough and detail-oriented. Absolutely recommend!',
    'rev.c1.name': 'Maria L. — Miami, FL',
    'rev.c2.text': 'Professional, on time and incredibly detailed. My house has never looked this clean. Will definitely book again!',
    'rev.c2.name': 'Jessica R. — Broward, FL',
    'rev.c3.text': 'Licensed, reliable and trustworthy. I feel completely comfortable having them in my home. Best cleaning service in Miami & Broward!',
    'rev.c3.name': 'Carmen T. — Miami, FL',
    'rev.badge': '✓ Verified Client Review',
    'rev.bottom': '✦ 18 locals recently requested a quote',
    // Contact Form
    'contact.title1': 'Ready for a',
    'contact.title2': 'Spotless Home?',
    'contact.t1': 'Free Estimate',
    'contact.t2': 'Same-Day Response',
    'contact.t3': 'Licensed & Insured',
    'contact.t4': 'Recurring Discounts Available',
    'contact.info': 'Contact Us Directly',
    'contact.f.name': 'Your Name',
    'contact.f.phone': 'Phone Number',
    'contact.f.service': 'Type of Service',
    'contact.f.s1': 'Standard Cleaning',
    'contact.f.s2': 'Deep Cleaning',
    'contact.f.s3': 'Airbnb Cleaning',
    'contact.f.s4': 'Move-In or Move-Out',
    'contact.f.s5': 'Office Cleaning',
    'contact.f.s6': 'Other',
    'contact.f.date': 'Preferred Date/Time',
    'contact.f.dateMobile': 'Preferred date & time (e.g. Monday morning)',
    'contact.submit': 'GET MY FREE ESTIMATE →',
    'contact.privacy': '🔒 Your info is private and never shared.',
    // WhatsApp
    'wa.tooltip': 'Chat with us! 💬',
    // Footer
    'footer.tagline': 'Premium cleaning services — licensed, reliable and detail-oriented.',
    'footer.s.title': 'Our Services',
    'footer.c.title': 'Contact Us',
    'footer.a.title': 'Service Area',
    'footer.a.desc': 'Proudly serving Miami and Broward, including Miami-Dade, Fort Lauderdale, Hollywood, Pembroke Pines, Kendall and surrounding areas.',
    'footer.rights': '© 2025 TwoGirls MopCo. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.backup': 'Backup'
  },
  es: {
    // Navbar
    'nav.home': 'Inicio',
    'nav.services': 'Servicios',
    'nav.about': 'Nosotros',
    'nav.reviews': 'Reseñas',
    'nav.contact': 'Contacto',
    'nav.quote': 'COTIZACIÓN GRATIS',
    // Brand
    'brand.tagline': 'SERVICIOS DE LIMPIEZA PREMIUM',
    // Hero
    'hero.badge': '✦ MIAMI Y BROWARD · DISPONIBILIDAD EL MISMO DÍA',
    'hero.title1': 'Tu Hogar, Limpio',
    'hero.title2': 'Con Cuidado Personal.',
    'hero.sub': 'Limpieza premium en Miami y Broward. Licenciadas, confiables y detallistas. Disponibilidad el mismo día — pregunta por descuentos.',
    'hero.btn1': '📱 RESERVA — COTIZACIÓN GRATIS',
    'hero.btn2': 'Ver Nuestros Servicios ↓',
    'hero.trust1': 'Licenciadas y Aseguradas',
    'hero.trust2': 'Disponible el Mismo Día',
    'hero.trust3': '100% Satisfacción Garantizada',
    // Trust Bar
    'trust.local': 'Licenciadas y Confiables',
    'trust.area': 'Miami y Broward',
    'trust.hours': 'Disponibilidad Inmediata',
    'trust.payment': 'Descuentos Frecuentes',
    'trust.services': '5 Servicios Disponibles',
    // Services
    'services.label': '✦ NUESTROS SERVICIOS ✦',
    'services.title': 'Limpieza Premium Para Cada Espacio',
    'services.sub': 'Licenciadas · Confiables · Detallistas',
    'services.c1.title': 'Limpieza Estándar',
    'services.c1.desc': 'Limpieza confiable y constante para mantener tu hogar impecable cada semana.',
    'services.c2.title': 'Limpieza Profunda',
    'services.c2.desc': 'Limpieza completa de arriba a abajo para un hogar renovado.',
    'services.c3.title': 'Limpieza para Airbnb',
    'services.c3.desc': 'Cambios rápidos con calidad de hotel para que tus huéspedes siempre lleguen a un espacio impecable.',
    'services.c4.title': 'Limpieza de Mudanza',
    'services.c4.desc': 'Deja tu espacio anterior impecable o llega a tu nuevo hogar limpio.',
    'services.c5.title': 'Limpieza de Oficinas',
    'services.c5.desc': 'Limpieza profesional y detallista para oficinas y espacios comerciales.',
    'services.cta': 'OBTÉN TU COTIZACIÓN GRATIS',
    // Before & After
    'ba.label': '✦ NUESTRO TRABAJO ✦',
    'ba.title': 'Resultados Reales. Hogares Reales.',
    'ba.sub': 'Mira la diferencia de TwoGirls MopCo en cada cuarto que tocamos.',
    'ba.stat1': 'Satisfacción Garantizada',
    'ba.stat2': 'Disponible el Mismo Día',
    'ba.stat3': 'Calidad Premium',
    'ba.cta': '📱 RESERVA TU LIMPIEZA HOY',
    // Why Choose Us
    'why.title1': 'Por Qué Miami y Broward Confía en',
    'why.title2': 'TwoGirls MopCo',
    'why.c1.title': 'Licenciadas y Aseguradas',
    'why.c1.desc': 'Totalmente licenciadas y aseguradas para tu completa tranquilidad.',
    'why.c2.title': 'Disponible el Mismo Día',
    'why.c2.desc': '¿Necesitas limpieza hoy? Lo hacemos posible.',
    'why.c3.title': 'Descuentos por Frecuencia',
    'why.c3.desc': 'Mientras más reservas, más ahorras. Pregunta por nuestros planes.',
    'why.c4.title': 'Orientadas al Detalle',
    'why.c4.desc': 'No se nos escapa ni un rincón. Cada espacio recibe toda nuestra atención, siempre.',
    // Special Offer
    'offer.title': 'Pregunta Por Nuestros Descuentos Frecuentes',
    'offer.sub': 'Mientras más reservas, más ahorras. Disponibilidad el mismo día en Miami y Broward.',
    'offer.cta': '📱 LLAMA (786) 660-3720',
    // Reviews
    'rev.based': 'Basado en reseñas verificadas de clientes',
    'rev.c1.text': '¡TwoGirls MopCo es lo mejor! Son muy detallistas y profesionales. ¡Totalmente recomendado!',
    'rev.c1.name': 'Maria L. — Miami, FL',
    'rev.c2.text': 'Profesionales, puntuales e increíblemente detallistas. Mi casa nunca se había visto tan limpia. ¡Definitivamente reservaré de nuevo!',
    'rev.c2.name': 'Jessica R. — Broward, FL',
    'rev.c3.text': 'Licenciadas, confiables y de confianza. Me siento completamente cómoda teniéndolas en mi casa. ¡El mejor servicio de limpieza en Miami y Broward!',
    'rev.c3.name': 'Carmen T. — Miami, FL',
    'rev.badge': '✓ Reseña Verificada',
    'rev.bottom': '✦ 18 vecinos pidieron cotización recientemente',
    // Contact Form
    'contact.title1': '¿Lista para un',
    'contact.title2': 'Hogar Impecable?',
    'contact.t1': 'Cotización Gratis',
    'contact.t2': 'Respuesta el Mismo Día',
    'contact.t3': 'Licenciadas y Aseguradas',
    'contact.t4': 'Descuentos por Frecuencia Disponibles',
    'contact.info': 'Contáctanos Directamente',
    'contact.f.name': 'Tu Nombre',
    'contact.f.phone': 'Número de Teléfono',
    'contact.f.service': 'Tipo de Servicio',
    'contact.f.s1': 'Limpieza Estándar',
    'contact.f.s2': 'Limpieza Profunda',
    'contact.f.s3': 'Limpieza para Airbnb',
    'contact.f.s4': 'Limpieza de Mudanza',
    'contact.f.s5': 'Limpieza de Oficinas',
    'contact.f.s6': 'Otro',
    'contact.f.date': 'Fecha/Hora Preferida',
    'contact.f.dateMobile': 'Fecha y hora preferida (ej. lunes en la mañana)',
    'contact.submit': 'OBTENER MI COTIZACIÓN →',
    'contact.privacy': '🔒 Tu información es privada y nunca compartida.',
    // WhatsApp
    'wa.tooltip': '¡Escríbenos! 💬',
    // Footer
    'footer.tagline': 'Servicios de limpieza premium — licenciadas, confiables y detallistas.',
    'footer.s.title': 'Nuestros Servicios',
    'footer.c.title': 'Contáctanos',
    'footer.a.title': 'Área de Servicio',
    'footer.a.desc': 'Orgullosamente sirviendo Miami y Broward, incluyendo Miami-Dade, Fort Lauderdale, Hollywood, Pembroke Pines, Kendall y áreas cercanas.',
    'footer.rights': '© 2025 TwoGirls MopCo. Todos los derechos reservados.',
    'footer.privacy': 'Política de Privacidad',
    'footer.terms': 'Términos de Servicio',
    'footer.backup': 'Alternativo'
  }
};
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export const LanguageProvider: React.FC<{
  children: ReactNode;
}> = ({
  children
}) => {
  const [lang, setLang] = useState<Lang>('en');
  const t = (key: string) => {
    return translations[lang][key] || key;
  };
  return <LanguageContext.Provider value={{
    lang,
    setLang,
    t
  }} data-id="element-152">
      {children}
    </LanguageContext.Provider>;
};
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};